import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { ArrowBack, RedeemOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import {
  reviewRedemptionRequest,
  updateRedemptionPayout,
} from "../../../services/rewards/rewards";
import { RedemptionRequestModel } from "../model/redemption_request";
import ReviewRedemptionDialog from "../component/redemption_requests_list/review_redemption_dialog";
import UpdatePayoutDialog from "../component/redemption_requests_list/update_payout_dialog";
import { black, purple, purpleLight, red } from "../../../theme/colors";

function SectionHeader({ children, mt = 4 }: { children: ReactNode; mt?: number }) {
  return (
    <Box mt={mt} mb={1.5}>
      <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

function listItem({ title, value }: { title: string; value: ReactNode }) {
  return (
    <Box>
      <Typography color="primary.main">{title}</Typography>
      <Typography>{value ?? "-"}</Typography>
    </Box>
  );
}

function statusChipColors(status: string) {
  switch (status) {
    case "Completed":
      return { bg: "#E2F0D9", color: "#2E7D32" };
    case "In Progress":
      return { bg: "#E3F2FD", color: "#1976d2" };
    case "Rejected":
    case "Failed":
      return { bg: "#FCE4E4", color: red };
    case "Pending":
    default:
      return { bg: "#FFF4CC", color: "#B26A00" };
  }
}

export default function RedemptionRequestDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useFeedback();
  const { hasPermission } = useUser();

  const [request, setRequest] = useState<RedemptionRequestModel | null>(
    (location.state?.redemptionRequest as RedemptionRequestModel) || null
  );

  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);

  const backToList = () => navigate("/rewards?currentTab=1");

  function closeReview() {
    if (reviewSubmitting) return;
    setReviewAction(null);
  }

  function confirmReview(comments: string) {
    if (!request || !reviewAction) return;
    setReviewSubmitting(true);
    const status = reviewAction === "approve" ? "In Progress" : "Rejected";
    reviewRedemptionRequest({ requestId: request.id, status, comments })
      .then(() => {
        showSnackbar(
          reviewAction === "approve" ? "Request moved to In Progress" : "Request rejected"
        );
        queryClient.invalidateQueries({ queryKey: ["redemptionRequestsListing"] });
        // Approving/rejecting moves points between a user's redeemed/available
        // balance, shown on the Reward Points list - that list wasn't
        // refreshing until a hard reload without this.
        queryClient.invalidateQueries({ queryKey: ["rewardList"] });
        setRequest((prev) =>
          prev
            ? {
                ...prev,
                status,
                adminComments: comments,
                reviewedAt: new Date().toISOString(),
                payoutStatus: status === "In Progress" ? "Pending" : prev.payoutStatus,
              }
            : prev
        );
        setReviewAction(null);
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
        }
      })
      .finally(() => setReviewSubmitting(false));
  }

  function closePayout() {
    if (payoutSubmitting) return;
    setPayoutDialogOpen(false);
  }

  function confirmPayout(payload: {
    payoutStatus: "Processing" | "Released" | "Failed";
    payoutReference?: string;
    payoutRemarks?: string;
  }) {
    if (!request) return;
    setPayoutSubmitting(true);
    updateRedemptionPayout({ requestId: request.id, ...payload })
      .then(() => {
        showSnackbar("Payout status updated");
        queryClient.invalidateQueries({ queryKey: ["redemptionRequestsListing"] });
        queryClient.invalidateQueries({ queryKey: ["rewardList"] });
        setRequest((prev) =>
          prev
            ? {
                ...prev,
                payoutStatus: payload.payoutStatus,
                payoutReference: payload.payoutReference ?? prev.payoutReference,
                payoutRemarks: payload.payoutRemarks ?? prev.payoutRemarks,
                status:
                  payload.payoutStatus === "Released"
                    ? "Completed"
                    : payload.payoutStatus === "Failed"
                    ? "Failed"
                    : prev.status,
                payoutReleasedAt:
                  payload.payoutStatus === "Released"
                    ? new Date().toISOString()
                    : prev.payoutReleasedAt,
              }
            : prev
        );
        setPayoutDialogOpen(false);
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
        }
      })
      .finally(() => setPayoutSubmitting(false));
  }

  return (
    <MainLayout>
      <Grid container spacing={3}>
        <Grid item md={8} lg={7} sm={12} xs={12}>
          <Card
            elevation={0}
            sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC", m: 1 }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Box display="flex" flexDirection="row" alignItems="center">
                <IconButton onClick={backToList}>
                  <ArrowBack />
                </IconButton>
                <Box display="flex" alignItems="center" gap={1.5} ml={0.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: purpleLight,
                      flexShrink: 0,
                    }}
                  >
                    <RedeemOutlined sx={{ color: purple, fontSize: 22 }} />
                  </Box>
                  <Typography variant="h6" fontWeight="600">
                    Redemption Request Details
                  </Typography>
                </Box>
              </Box>

              <Box m={2}>
                {!request ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    py={8}
                  >
                    <Typography fontWeight={600} color={black}>
                      This request's details aren't available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Open this page from the User Requested Rewards list to view its details.
                    </Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={backToList}>
                      Back To Rewards
                    </Button>
                  </Box>
                ) : (
                  <Box overflow="auto" flex={1}>
                    <SectionHeader mt={1}>Request Details</SectionHeader>
                    <Grid container spacing={3}>
                      <Grid item md={6}>
                        {listItem({ title: "Request ID", value: request.id })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({ title: "UID", value: request.userId })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({ title: "Full Name", value: request.userName })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({ title: "Contact Number", value: request.mobileNo })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({ title: "Points Requested", value: request.points })}
                      </Grid>
                      <Grid item md={6}>
                        {listItem({
                          title: "Requested On",
                          value: request.createdAt
                            ? dayjs(request.createdAt).format("DD MMM YYYY, hh:mm A")
                            : "-",
                        })}
                      </Grid>
                      <Grid item md={6}>
                        <Typography color="primary.main">Status</Typography>
                        <Chip
                          label={request.status}
                          size="small"
                          sx={{
                            mt: 0.5,
                            bgcolor: statusChipColors(request.status).bg,
                            color: statusChipColors(request.status).color,
                            fontWeight: 600,
                          }}
                        />
                      </Grid>
                    </Grid>

                    {request.status !== "Pending" && (
                      <>
                        <SectionHeader>Review Details</SectionHeader>
                        <Grid container spacing={3}>
                          <Grid item md={6}>
                            {listItem({
                              title: "Reviewed At",
                              value: request.reviewedAt
                                ? dayjs(request.reviewedAt).format("DD MMM YYYY, hh:mm A")
                                : "-",
                            })}
                          </Grid>
                          <Grid item md={12}>
                            {listItem({ title: "Admin Comments", value: request.adminComments })}
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {request.payoutStatus && (
                      <>
                        <SectionHeader>Payout Details</SectionHeader>
                        <Grid container spacing={3}>
                          <Grid item md={6}>
                            <Typography color="primary.main">Payout Status</Typography>
                            <Chip
                              label={request.payoutStatus}
                              size="small"
                              sx={{
                                mt: 0.5,
                                bgcolor: statusChipColors(request.payoutStatus).bg,
                                color: statusChipColors(request.payoutStatus).color,
                                fontWeight: 600,
                              }}
                            />
                          </Grid>
                          <Grid item md={6}>
                            {listItem({
                              title: "Payout Released At",
                              value: request.payoutReleasedAt
                                ? dayjs(request.payoutReleasedAt).format("DD MMM YYYY, hh:mm A")
                                : "-",
                            })}
                          </Grid>
                          <Grid item md={6}>
                            {listItem({ title: "Payout Reference", value: request.payoutReference })}
                          </Grid>
                          <Grid item md={6}>
                            {listItem({ title: "Payout Remarks", value: request.payoutRemarks })}
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {request.status === "Pending" && hasPermission("rewards", "update") && (
                      <Box display="flex" gap={2} mt={4}>
                        <Button
                          variant="contained"
                          sx={{ minWidth: 150 }}
                          onClick={() => setReviewAction("approve")}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{ minWidth: 150 }}
                          onClick={() => setReviewAction("reject")}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}

                    {request.status === "In Progress" && hasPermission("rewards", "update") && (
                      <Box mt={4}>
                        <Button
                          variant="contained"
                          sx={{ minWidth: 190 }}
                          onClick={() => setPayoutDialogOpen(true)}
                        >
                          Update Payout Status
                        </Button>
                      </Box>
                    )}

                    <Divider sx={{ backgroundColor: "#F5E6F5", my: 3 }} />
                    <Grid item md={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Button
                          variant="outlined"
                          sx={{
                            pl: 2,
                            pr: 2,
                            textTransform: "capitalize",
                            fontWeight: 600,
                            minWidth: 170,
                          }}
                          onClick={backToList}
                        >
                          Back To Rewards
                        </Button>
                      </Box>
                    </Grid>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ReviewRedemptionDialog
        open={!!reviewAction}
        onClose={closeReview}
        onConfirm={confirmReview}
        action={reviewAction}
        loading={reviewSubmitting}
      />

      <UpdatePayoutDialog
        open={payoutDialogOpen}
        onClose={closePayout}
        onConfirm={confirmPayout}
        loading={payoutSubmitting}
      />
    </MainLayout>
  );
}
